import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Grid, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { FormInput, QPFQuickRegisterForm, SwitchInput } from "@4uhub/lib4uhub";

import { TFormType } from "../../../../models/form";
import { IUserGet } from "../../../../models/users";

import { CPFMask } from "../../../../utils/maks";

import { IEstablishment } from "@4uhub/lib4uhub";
import MainUserEstablishment from "./MainUserEstablishment";
import { useWatch } from "react-hook-form";
import { useFormContext } from "@4uhub/lib4uhub";
import { useEffect } from "react";
import { useInstalledExtensions } from "../../../../hooks/useInstalledExtensions";

const translationPath = "page.register.users.";

const getIndividualIntegrationId = (
  individual?: {
    integrationId?: string | number | null;
    identifier?: string | number | null;
  } | null
) => individual?.integrationId ?? individual?.identifier ?? "";

const UserBasicData = ({
  user,
  type,
  establishmentsList,
}: {
  user?: IUserGet;
  type: TFormType;
  establishmentsList: IEstablishment[];
}) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext();
  const [hasTasyEmailExtension] = useInstalledExtensions("27");

  const individual = useWatch({
    name: "individual",
    control,
  });
  const individualIntegrationId = getIndividualIntegrationId(individual);
  const userIndividualIntegrationId = getIndividualIntegrationId(
    user?.individual
  );

  useEffect(() => {
    if ((!getValues("userName") || type === "create") && individual?.document) {
      setValue("userName", individual.document.replace(/[^\w\s]/gi, ""));
    }
  }, [individual?.document, setValue, getValues, type]);

  useEffect(() => {
    const incoming = (individual?.mail?.toString() ?? "").trim().toLowerCase();
    const current = (getValues("email") as string | undefined)?.trim();

    if (!current || type === "create") {
      setValue("email", incoming, { shouldValidate: true, shouldDirty: true });
    }
  }, [individual?.mail, type, getValues, setValue]);

  return (
    <>
      <Grid item xs={12} sm={12} textAlign={"end"}>
        <SwitchInput
          name="enable"
          label={t("page.register.users.enable") || "Enable"}
        />
      </Grid>

      {type === "create" && (
        <Grid item xs={12} sm={6}>
          <QPFQuickRegisterForm
            label={t("page.register.users.individual") || "Individual"}
            name="individual"
          />
        </Grid>
      )}

      <Grid item xs={12} sm={user ? 4 : 6}>
        <FormInput
          size="small"
          autoFocus={true}
          name="userName"
          label={t("page.register.users.username") || "Username"}
          fullWidth
        />
      </Grid>

      {user && (
        <Grid
          item
          xs={12}
          sm={8}
          alignItems={"center"}
          display={"flex"}
          gap={2}
          flexWrap={"wrap"}
        >
          <div>
            <Typography color={(theme) => theme.palette.text.secondary}>
              {t(translationPath + "name")}:{" "}
            </Typography>
            <Typography>{user.individual?.name}</Typography>
          </div>
          <div>
            <Typography color={(theme) => theme.palette.text.secondary}>
              {t(translationPath + "document")}:{" "}
            </Typography>
            <Typography>
              {user.individual?.code && CPFMask.resolve(user.individual.code)}
            </Typography>
          </div>
          <div>
            <Typography color={(theme) => theme.palette.text.secondary}>
              {t(translationPath + "integration_id")}:{" "}
            </Typography>
            <Typography>{userIndividualIntegrationId || "-"}</Typography>
          </div>
        </Grid>
      )}

      {type === "create" && individual && (
        <Grid item xs={12} sm={6}>
          <TextField
            size="small"
            label={t(translationPath + "integration_id")}
            value={individualIntegrationId || "-"}
            fullWidth
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>
      )}

      {type === "create" && (
        <Grid item xs={12} sm={6}>
          <FormInput
            size="small"
            name="password"
            type="password"
            fullWidth
            label={t("page.register.users.password") || "Password"}
          />
        </Grid>
      )}

      <Grid item xs={12} sm={user ? 12 : 6}>
        <FormInput
          size="small"
          name="email"
          label={t("page.register.users.email") || "Email"}
          fullWidth
        />
        {hasTasyEmailExtension && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 1,
              px: 1.5,
              py: 1,
              backgroundColor: "#FEFF99",
              color: (theme) => theme.palette.warning.dark,
              borderRadius: 1,
              border: (theme) =>
                `1px solid ${theme.palette.warning.main}`,
            }}
          >
            <WarningAmberIcon fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {t(translationPath + "email_tasy_warning") ||
                "This is an informational field. Changes made here have no effect. Please change the email address in Tasy."}
            </Typography>
          </Box>
        )}
        {user && !user.emailConfirmed && (
          <Stack marginLeft={3}>
            <Typography color={"error"} variant="body2">
              {t("page.register.users.email_not_confirmed") ||
                "Email not confirmed by user"}
            </Typography>
          </Stack>
        )}
      </Grid>

      <MainUserEstablishment
        user={user}
        type={type}
        establishmentsList={establishmentsList}
      />
    </>
  );
};

export default UserBasicData;
